import { useMemo } from "react";
import { GovSettingsOn, GovTask } from "../../../../utils/icGovernance";
import { useGovernance } from "../GovernanceLayout";
import {
  PriorityChip,
  SignOffChip,
  StatusChip,
  clientLabel,
  dateOnly,
  hours,
  hoursVariance,
  linkedIds,
  vendorLabel,
} from "../governanceStatus";
import {
  EvidenceLinks,
  IdLink,
  LinkedIdList,
  LongText,
  PartyChip,
  PercentBar,
  PlainText,
  TitleLink,
  VarianceText,
  localDay,
} from "./cells";
import RegisterTable, { RegisterColumn, WaitingNotice } from "./RegisterTable";
import { useRegisterItems } from "./useRegisterItems";

// Master Project Tracker (TASK) — columns in the client's workbook order.
const trackerColumns = (s: GovSettingsOn): RegisterColumn<GovTask>[] => [
  { key: "id", header: "Task ID", render: (r) => <IdLink item={r} /> },
  { key: "workstream", header: "Workstream", render: (r) => <PlainText value={r.workstream} /> },
  { key: "title", header: "Task", wrap: true, minWidth: 220, render: (r) => <TitleLink item={r} /> },
  {
    key: "acceptance",
    header: "Description / Acceptance Criteria",
    wrap: true,
    minWidth: 280,
    render: (r) => <LongText value={r.acceptanceCriteria || r.description} />,
  },
  { key: "leadParty", header: "Lead Party", render: (r) => <PartyChip party={r.leadParty} settings={s} /> },
  { key: "clientOwner", header: `${clientLabel(s)} Lead / Role`, render: (r) => <PlainText value={r.clientOwner} /> },
  { key: "vendorOwner", header: `${vendorLabel(s)} Lead / Role`, render: (r) => <PlainText value={r.vendorOwner} /> },
  { key: "priority", header: "Priority", render: (r) => <PriorityChip priority={r.priority} /> },
  { key: "startDate", header: "Start Date", render: (r) => dateOnly(r.startDate) },
  { key: "status", header: "Status", render: (r) => <StatusChip kind="TASK" status={r.status} /> },
  { key: "percent", header: "% Complete", render: (r) => <PercentBar value={r.percentComplete} /> },
  { key: "dueDate", header: "Date Due", render: (r) => dateOnly(r.dueDate) },
  { key: "estHours", header: "Est. Hours", align: "right", render: (r) => hours(r.estimatedMinutes) },
  { key: "actualHours", header: "Actual Hours", align: "right", render: (r) => hours(r.actualMinutes) },
  {
    key: "variance",
    header: "Hours Variance",
    align: "right",
    render: (r) => <VarianceText text={hoursVariance(r.actualMinutes, r.estimatedMinutes)} />,
  },
  { key: "related", header: "Dependency / Related ID", render: (r) => <LinkedIdList ids={linkedIds(r)} /> },
  {
    key: "evidence",
    header: "Deliverable / Evidence Link",
    minWidth: 160,
    render: (r) => <EvidenceLinks evidence={r.evidence} />,
  },
  { key: "signOffBy", header: `${clientLabel(s)} Sign-Off By`, render: (r) => <PlainText value={r.signOff?.decidedBy} /> },
  { key: "signOffStatus", header: "Sign-Off Status", render: (r) => <SignOffChip status={r.signOff?.status} /> },
  { key: "signOffDate", header: "Sign-Off Date", render: (r) => localDay(r.signOff?.decidedAt) },
];

const WAITING: WaitingNotice<GovTask> = {
  reason: (r) => (r.signOff?.status === "PENDING" ? "sign-off" : null),
  describe: (n) => `${n} ${n === 1 ? "task waits" : "tasks wait"} for your sign-off`,
};

const TrackerRegister = () => {
  const { settings } = useGovernance();
  const register = useRegisterItems<GovTask>("TASK");
  const columns = useMemo(() => trackerColumns(settings), [settings]);
  return (
    <RegisterTable
      {...register}
      kind="TASK"
      columns={columns}
      noun={{ one: "task", many: "tasks" }}
      caption={`${vendorLabel(settings)} maintains; ${clientLabel(settings)} signs off`}
      waiting={WAITING}
    />
  );
};

export default TrackerRegister;
